import api from "../utils/axiosConfig";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Header from "./Header";
import { useAuth } from "../auth/useAuth.hook";
import {
  FormErrors,
  emptyStateAgentForm,
  EditStateType,
} from "../utils/interfaces/AgentForm";
import {
  capitalizeFirstLetter,
  cpfMask,
  phoneMask,
  toastOptions,
} from "../utils/helpers";
import {
  isValidCPF,
  isValidPhone,
  isValidEmail,
  validatePixKey,
} from "../utils/pixValidation";

const apiUri = import.meta.env.VITE_API_URI as string;

const AgentEditForm: React.FC = () => {
  const [formData, setFormData] = useState<EditStateType>({
    cpf: "",
    name: "",
    fathersName: "",
    mothersName: "",
    email: "",
    phone: "",
    birthDate: "",
    gender: "",
    pixKey: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const { accessToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [pixType, setPixType] = useState<string>("");

  const getAgent = async () => {
    try {
      const { data } = await api.get(`agents/me`);
      setFormData({ ...emptyStateAgentForm, ...data });
    } catch (error) {
      console.error("Error fetching agent data:", error);
    }
  };

  useEffect(() => {
    if (accessToken) {
      getAgent();
    }
  }, [accessToken]);

  const handleChange = async (
    e:
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    if (name === "phone") {
      setFormData({ ...formData, [name]: phoneMask(value) });
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

  const updateAgent = async () => {
    setIsLoading(true);
    const response = await api.put(
      `${apiUri}agents/me`,
      {
        pixKey: formData.pixKey && /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(formData.pixKey.trim())
          ? formData.pixKey.replace(/\D/g, '')
          : formData.pixKey,
        phone: formData.phone,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    setIsLoading(false);
    return response;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (await validateForm()) {
      try {
        await updateAgent();
        toast.success("Informações atualizadas com sucesso!", toastOptions);
      } catch (error) {
        toast.error("Erro ao atualizar as informações", toastOptions);
      }
    }
  };

  const validateForm = async () => {
    const newErrors: any = {};

    // Validar e-mail
    if (!isValidEmail(formData.email)) {
      newErrors.email = "E-mail inválido";
    }

    // Validar CPF
    if (!isValidCPF(formData.cpf)) {
      newErrors.cpf = "CPF inválido";
    }

    // Validar telefone
    if (!isValidPhone(formData.phone)) {
      newErrors.phone = "Telefone inválido (DDD + número)";
    }

    // Validar chave PIX
    if (pixType && formData.pixKey) {
      const pixValidation = validatePixKey(formData.pixKey, pixType);
      if (!pixValidation.isValid) {
        newErrors.pixKey = pixValidation.error;
      }
    }

    // Atualizar estado de erros para exibir na UI
    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handlePixTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPixType(e.target.value);
  };

  const handlePixKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    
    if (pixType === "Telefone") {
      // Remove formatação anterior
      value = value.replace(/\D/g, "");
      // Adiciona +55 se necessário
      if (!value.startsWith("55")) {
        value = "55" + value;
      }
      // Limita a 13 dígitos (55 + 11 dígitos do telefone)
      if (value.length > 13) {
        value = value.slice(0, 13);
      }
      setFormData({ ...formData, pixKey: value });
      return;
    }
    
    if (pixType === "CPF") {
      value = value.replace(/\D/g, "");
      if (value.length > 11) {
        value = value.slice(0, 11);
      }
      setFormData({ ...formData, pixKey: value });
      return;
    }

    // E-mail e Chave Aleatória: sem formatação especial
    setFormData({ ...formData, pixKey: value });
  };

  return (
    <>
      <div>{accessToken && <Header />}</div>
      <div className="min-h-screen flex flex-col items-stretch md:items-center w-full">
        <form
          onSubmit={handleSubmit}
          className="bg-white w-screen max-w-none min-h-[calc(100vh-5rem)] p-0 shadow-none mt-0 mb-0 rounded-none border-0 overflow-hidden
          md:max-w-4xl md:mx-auto md:shadow-sm md:mt-8 md:mb-8 md:rounded-xl md:border md:border-slate-100 md:min-h-0 md:w-auto"
        >
          <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-2.5">
            <span aria-hidden className="h-2 w-2 rounded-full shrink-0 bg-blue-400"></span>
            <h2 className="text-sm md:text-base font-semibold tracking-tight text-slate-600">
              Informações Pessoais
            </h2>
          </div>

          <div className="p-3 md:p-6 pb-4 md:pb-6">
            <div className="grid grid-cols-12 gap-2 md:gap-5">
              <div className="col-span-12 md:col-span-6 mt-1.5 md:mt-3">
                <label className="block text-xs font-medium text-preppoBlue mb-1">
                  Nome*
                </label>
                <input
                  type="text"
                  name="name"
                  maxLength={100}
                  minLength={3}
                  className="w-full h-9 md:h-10 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 px-3 bg-slate-50 text-sm md:text-xs lg:text-sm text-preppoBlue"
                  value={capitalizeFirstLetter(formData.name)}
                  onChange={handleChange}
                  disabled
                  required
                />
                {errors.name && (
                  <span className="text-red-500 text-sm">{errors.name}</span>
                )}
              </div>
              <div className="col-span-12 md:col-span-3 mt-1.5 md:mt-3">
                <label className="block text-xs font-medium text-preppoBlue mb-1">
                  CPF*
                </label>
                <input
                  type="text"
                  name="cpf"
                  maxLength={14}
                  minLength={11}
                  className="w-full h-9 md:h-10 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 px-3 bg-slate-50 text-sm md:text-xs lg:text-sm text-preppoBlue"
                  value={cpfMask(formData.cpf)}
                  onChange={handleChange}
                  disabled
                  required
                />
                {errors.cpf && (
                  <span className="text-red-500 text-sm">{errors.cpf}</span>
                )}
              </div>
              <div className="col-span-12 md:col-span-3 mt-1.5 md:mt-3">
                <label className="block text-xs font-medium text-preppoBlue mb-1">
                  Nascimento*
                </label>
                <input
                  type="date"
                  name="birthDate"
                  className="w-full h-9 md:h-10 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 px-3 bg-slate-50 text-sm md:text-xs lg:text-sm text-preppoBlue"
                  value={formData.birthDate}
                  onChange={handleChange}
                  disabled
                  required
                />
                {errors.birthDate && (
                  <span className="text-red-500 text-sm">
                    {errors.birthDate}
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-12 gap-2 md:gap-5 mt-2 md:mt-0">
              <div className="col-span-12 md:col-span-6 mt-1.5 md:mt-3">
                <label className="block text-xs font-medium text-preppoBlue mb-1">
                  Nome do Pai
                </label>
                <input
                  type="text"
                  name="fathersName"
                  maxLength={100}
                  minLength={3}
                  className="w-full h-9 md:h-10 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 px-3 bg-slate-50 text-sm md:text-xs lg:text-sm text-preppoBlue"
                  value={capitalizeFirstLetter(formData.fathersName)}
                  onChange={handleChange}
                  disabled
                />
              </div>

              <div className="col-span-12 md:col-span-6 mt-1.5 md:mt-3">
                <label className="block text-xs font-medium text-preppoBlue mb-1">
                  Nome da Mãe
                </label>
                <input
                  type="text"
                  name="mothersName"
                  maxLength={100}
                  minLength={3}
                  className="w-full h-9 md:h-10 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 px-3 bg-slate-50 text-sm md:text-xs lg:text-sm text-preppoBlue"
                  value={capitalizeFirstLetter(formData.mothersName)}
                  onChange={handleChange}
                  disabled
                />
              </div>
            </div>

            <div className="grid grid-cols-12 gap-2 md:gap-5 mt-2 md:mt-0">
              <div className="col-span-12 md:col-span-6 mt-1.5 md:mt-3">
                <label className="block text-xs font-medium text-preppoBlue mb-1">
                  Gênero*
                </label>
                <select
                  className="w-full h-9 md:h-10 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 px-3 bg-slate-50 text-sm md:text-xs lg:text-sm text-preppoBlue"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  required
                  disabled
                >
                  <option value="">Selecionar</option>
                  <option value="male">Masculino</option>
                  <option value="female">Feminino</option>
                  <option value="other">Prefiro não informar</option>
                </select>
                {errors.gender && (
                  <span className="text-red-500 text-sm">{errors.gender}</span>
                )}
              </div>
              <div className="col-span-12 md:col-span-6 mt-1.5 md:mt-3">
                <label className="block text-xs font-medium text-preppoBlue mb-1">
                  E-mail*
                </label>
                <input
                  type="email"
                  name="email"
                  maxLength={50}
                  minLength={3}
                  className="w-full h-9 md:h-10 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 px-3 bg-slate-50 text-sm md:text-xs lg:text-sm text-preppoBlue"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled
                />
                {errors.email && (
                  <span className="text-red-500 text-sm">{errors.email}</span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-12 gap-2 md:gap-5 mt-2 md:mt-0 text-preppoBlue">
              <div className="col-span-12 md:col-span-6 mt-1.5 md:mt-3">
                <label className="block text-xs font-medium text-preppoBlue mb-1">
                  Telefone*
                </label>
                <input
                  type="text"
                  name="phone"
                  maxLength={20}
                  minLength={3}
                  className="w-full h-9 md:h-10 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 px-3 bg-slate-50 text-sm md:text-xs lg:text-sm text-preppoBlue"
                  value={phoneMask(formData.phone)}
                  onChange={handleChange}
                  required
                />
                {errors.phone && (
                  <span className="text-red-500 text-sm">{errors.phone}</span>
                )}
              </div>
              <div className="col-span-12 md:col-span-6 mt-1.5 md:mt-3">
                <label className="block text-xs font-medium text-preppoBlue mb-1 md:mb-2">
                  Tipo chave PIX*
                </label>
                <div className="inline-flex rounded-xl border border-slate-200 overflow-hidden">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="pixType"
                      value="Telefone"
                      checked={pixType === "Telefone"}
                      onChange={handlePixTypeChange}
                      className="sr-only"
                    />
                    <span className="px-2.5 py-1.5 md:px-3 md:py-2 text-xs aria-pressed:bg-blue-600 aria-pressed:text-white" aria-pressed={pixType === "Telefone"}>
                      Telefone
                    </span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="pixType"
                      value="CPF"
                      checked={pixType === "CPF"}
                      onChange={handlePixTypeChange}
                      className="sr-only"
                    />
                    <span className="px-2.5 py-1.5 md:px-3 md:py-2 text-xs aria-pressed:bg-blue-600 aria-pressed:text-white" aria-pressed={pixType === "CPF"}>
                      CPF
                    </span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="pixType"
                      value="E-mail"
                      checked={pixType === "E-mail"}
                      onChange={handlePixTypeChange}
                      className="sr-only"
                    />
                    <span className="px-2.5 py-1.5 md:px-3 md:py-2 text-xs aria-pressed:bg-blue-600 aria-pressed:text-white" aria-pressed={pixType === "E-mail"}>
                      E-mail
                    </span>
                  </label>
                </div>
              </div>

              <div className="col-span-12 mt-2">
                <div className="flex items-start gap-2 rounded-lg border border-amber-100 bg-amber-50/60 px-3 py-2 text-[13px] leading-snug text-amber-800">
                  <span aria-hidden className="text-amber-500 text-sm mt-0.5 shrink-0">⚠️</span>
                  <p className="text-xs md:text-[13px]">
                    <span className="font-semibold">Atenção:</span> não é
                    permitido cadastrar chave PIX de terceiros ou de pessoas
                    jurídicas. A chave PIX deve estar em seu nome (CPF).
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-2 md:gap-5 mt-2 md:mt-0">
              <div className="col-span-12 mt-1.5 md:mt-3">
                <label className="block text-xs font-medium text-preppoBlue mb-1">
                  Chave PIX*
                </label>
                <input
                  type="text"
                  name="pixKey"
                  minLength={3}
                  className="w-full h-9 md:h-10 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 px-3 bg-slate-50 text-sm md:text-xs lg:text-sm text-preppoBlue"
                  value={formData.pixKey}
                  onChange={handlePixKeyChange}
                  required
                />
                {errors.pixKey && (
                  <span className="text-red-500 text-sm">{errors.pixKey}</span>
                )}
              </div>
            </div>
          </div>
          <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-3 flex justify-end">
            <button
              type="submit"
              className="h-8 md:h-10 px-3 md:px-5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-sm hover:shadow transition disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? "Atualizando..." : "Atualizar"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AgentEditForm;
